import { Link } from 'react-router-dom';
import type { ServerCredential, ServerLogin } from '../api/types.js';
import { useCredentials, useStartLogin } from '../hooks/use-credentials.js';
import { Button } from './button.js';

// The logins a server's sessions depend on (ADR-033): when each ends, and Log in again once it is close. Quiet while every
// login has time left; the one ending within 30 minutes, or ended, carries the colour and the button. The server starts
// the device-code login; its URL and code show here until the operator has approved it.

const SOON_MS = 30 * 60_000;
const LABEL: Record<ServerCredential['kind'], string> = { 'aws-sso': 'AWS SSO', tailscale: 'Tailscale' };

/** « in 3 h », « in 25 min », « 2 h ago » */
function relative(ms: number): string {
  const abs = Math.abs(ms);
  const text = abs >= 2 * 86_400_000 ? `${Math.round(abs / 86_400_000)} days` : abs >= 3_600_000 ? `${Math.round(abs / 3_600_000)} h` : `${Math.max(1, Math.round(abs / 60_000))} min`;
  return ms >= 0 ? `in ${text}` : `${text} ago`;
}

function at(iso: string, now: number): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date(now).toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** What Log in again did: the URL and code to approve, then logged in or why not */
function LoginProgress({ login }: { login: ServerLogin }) {
  if (login.status === 'done') return <span style={{ color: 'var(--color-success)' }}>Logged in. The sessions waiting on it go on.</span>;
  if (login.status === 'failed') return <span style={{ color: 'var(--color-error)' }}>{login.error ?? 'The login failed.'}</span>;
  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span>Approve it with the account of this login:</span>
      {login.url && <a href={login.url} target="_blank" rel="noreferrer" className="break-all underline" style={{ color: 'var(--color-brand-light)' }}>{login.url}</a>}
      {login.code && <span>code <code className="rounded px-1.5 py-0.5 tracking-wider" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text)' }}>{login.code}</code></span>}
    </span>
  );
}

function Login({ serverId, c, login, now }: { serverId: string; c: ServerCredential; login?: ServerLogin; now: number }) {
  const start = useStartLogin(serverId);
  const left = Date.parse(c.expiresAt) - now;
  const state = left <= 0 ? 'ended' : left <= SOON_MS ? 'soon' : 'ok';
  const tone = state === 'ended' ? 'var(--color-error)' : state === 'soon' ? 'var(--color-warn)' : 'transparent';
  // The answer of the click shows at once; the page's reading takes over from there
  const shown = login ?? start.data;
  const waiting = shown?.status === 'waiting';
  return (
    <li className="py-2 pl-3" style={{ borderLeft: `2px solid ${tone}` }} data-testid="server-login">
      <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
        <span style={{ color: 'var(--color-text)' }}>{LABEL[c.kind]} {c.name}</span>
        <span className="tabular-nums" style={{ color: state === 'ok' ? 'var(--color-text-3)' : tone }}>
          {state === 'ended' ? `expired at ${at(c.expiresAt, now)}, ${relative(left)}` : `expires at ${at(c.expiresAt, now)}, ${relative(left)}`}
        </span>
      </p>
      {(state !== 'ok' || shown) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" style={{ color: 'var(--color-text-2)' }}>
          {!waiting && shown?.status !== 'done' && (
            <Button size="sm" onClick={() => start.mutate({ kind: c.kind, name: c.name })} disabled={start.isPending}>
              {start.isPending ? 'Starting the login…' : 'Log in again'}
            </Button>
          )}
          {shown && <LoginProgress login={shown} />}
          {start.error && !shown && <span style={{ color: 'var(--color-error)' }}>{`Could not start it: ${(start.error as Error).message}`}</span>}
        </div>
      )}
    </li>
  );
}

export function ServerLogins({ serverId, enabled }: { serverId: string; enabled: boolean }) {
  const { data, dataUpdatedAt } = useCredentials(serverId, enabled);
  const credentials = data?.credentials ?? [];
  if (!credentials.length) return null;
  // As of the last reading: the times move with it, every minute (every 3 s while a login waits)
  const now = dataUpdatedAt;
  return (
    <section className="mb-6" aria-labelledby="server-logins">
      <h2 id="server-logins" className="mb-1 text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Logins</h2>
      <ul>
        {credentials.map((c) => (
          <Login key={`${c.kind}:${c.name}`} serverId={serverId} c={c} now={now} login={data?.logins?.find((l) => l.kind === c.kind && l.name === c.name)} />
        ))}
      </ul>
    </section>
  );
}

/**
 * The next login of the server to end, for a session's header (ADR-033): quiet with time left, in colour within 30
 * minutes, red once ended, linking to the server page where Log in again is. Nothing when no login is known.
 */
export function NextLogin({ serverId, enabled }: { serverId: string; enabled: boolean }) {
  const { data, dataUpdatedAt } = useCredentials(serverId, enabled);
  // The agent lists them soonest end first
  const next = data?.credentials?.[0];
  if (!next) return null;
  const left = Date.parse(next.expiresAt) - dataUpdatedAt;
  const tone = left <= 0 ? 'var(--color-error)' : left <= SOON_MS ? 'var(--color-warn)' : 'var(--color-text-3)';
  return (
    <Link
      to={`/servers/${serverId}`}
      className="rounded text-xs underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
      style={{ color: tone, outlineColor: 'var(--color-brand)' }}
      data-testid="next-login"
    >
      {`${LABEL[next.kind]} ${next.name} ${left <= 0 ? 'expired at' : 'expires at'} ${at(next.expiresAt, dataUpdatedAt)}`}
    </Link>
  );
}
